import * as React from 'react';
import { useEffect } from 'react';

import { AdminUserRoles_ScopeLevel as AdminUserRolesScopeLevel } from '__generated__/graphql-types';
import type { AdminUserRoles_UserRole as AdminUserRole } from '__generated__/graphql-types-do-not-use';

import { Tbody, Td, Tr } from 'bundles/admin-shared/components/styledTableComponents';

import _t from 'i18n!nls/staff-management';

import { StaffPermissionsLoadingEventName } from '../types/eventingTypes';
import { trackLoadingTime } from '../utils/eventingUtils';
import { useStaffPermissionsEventing } from './StaffPermissionsEventing';

interface Props {
  adminUserRoles: AdminUserRole[];
}

// the lint warnings of about replacing the term "course" with custom labels don't apply here
// since the term "course" represents the scope level instead of the course label
const getScopeLevelStrings = (scopeLevel: AdminUserRolesScopeLevel) => {
  switch (scopeLevel) {
    case AdminUserRolesScopeLevel.Course:
      // eslint-disable-next-line no-restricted-syntax
      return _t('Course');
    case AdminUserRolesScopeLevel.Degree:
      return _t('Degree');
    case AdminUserRolesScopeLevel.Group:
      // eslint-disable-next-line no-restricted-syntax
      return _t('Course Session');
    case AdminUserRolesScopeLevel.Partner:
    default:
      return _t('Institution');
  }
};

const AdminUserRolesTableBody = ({ adminUserRoles }: Props) => {
  const { eventData, targetUserId, staffPermissionsViewLoadedTime } = useStaffPermissionsEventing();
  useEffect(() => {
    if (!!eventData && !!staffPermissionsViewLoadedTime && !!targetUserId) {
      trackLoadingTime(
        eventData,
        Date.now() - staffPermissionsViewLoadedTime,
        Number(targetUserId),
        StaffPermissionsLoadingEventName.UserRoles,
        adminUserRoles.length === 0
      );
    }
    // the side effect of eventing is intended to be executed only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Tbody>
      {adminUserRoles.map((role: AdminUserRole) => (
        <Tr key={role.id}>
          <Td>{getScopeLevelStrings(role.scopeLevel)}</Td>
          <Td>
            {role.scopeLevel === AdminUserRolesScopeLevel.Group && role.parentScopeName
              ? `${role.parentScopeName} - ${role.scopeName}`
              : role.scopeName}
          </Td>
          <Td>{role.roleName}</Td>
        </Tr>
      ))}
    </Tbody>
  );
};

export default AdminUserRolesTableBody;
